import React, { Fragment } from "react";
import {
  withStyles,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@material-ui/core";
import {
  ExpandMore,
  KeyboardArrowLeft as ArrowLeft,
  KeyboardArrowRight as ArrowRight,
  Lens as DotIcon
} from "@material-ui/icons";
import {
  ToggleButton,
  ToggleButtonGroup,
} from "@material-ui/lab";
import { compose } from "recompose";
import Select from "./components/Select";
import DataSourceList from "../../../dataBinding/DataSourceList";
import DataWrapperComponent from "../../../dataBinding/DataWrapper";
import {
  generateQuickFilterString,
  iconList,
  clearFilter,
} from "../../../common/filterUtilities";

import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import MergeObjects from "lodash/merge";
import Tooltip from "../../../common/StyledToolTip";
import appConstants from "../../../../appConstants";
import { trackEvent } from "../../../common/AppInsightsEventTracking";
import styles from "./styles";
import TimeRange from "./components/TimeRange";

class QuickFilter extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterObject: {},
      quickFilterIsVisible: true,
      isLoading: false,
    };
    this.initialWidth = this.props.glContainer.width;
  }

  componentDidMount() {
    this.props.glEventHub.on(
      "toggleQuickFilterVisibility",
      this.toggleQuickFilterVisibility
    );
    const terminalId = this.props.userData.userProfile.terminal.id;
    this._isComponentMounted = true;

    this.setState({
      isLoading: true,
      filterObject: {},
    });

    getDataWithQueryString("GetQuickFilters", `terminalId=${terminalId}`).then(
      (response) => {
        if (this._isComponentMounted) {
          if (response.status === 200) {
            const filterGroupStates = {};
            let filterObject = {};
            response.data.map((filter) => {
              if (
                filter.filterJson &&
                [null, this.props.filterName ?? ""].includes(filter.filterName)
              ) {
                filterObject = MergeObjects(
                  JSON.parse(filter.filterJson),
                  filterObject
                );
              }
            });
            // Defualt keep open all filter groups
            if (filterObject.filterGroups) {
              Object.keys(filterObject.filterGroups).forEach(
                (key) => (filterGroupStates[`${key}_isOpen`] = true)
              );
            }

            this.setState(
              {
                ...filterGroupStates,
                filterObject,
                isLoading: false,
              },
              this.updateFilter()
            );
          } else {
            this.setState(
              {
                filterObject: null,
                isLoading: false,
              },
              this.updateFilter()
            );
          }
        }
      },
      (error) => {
        if (this._isComponentMounted) {
          this.setState(
            {
              filterObject: null,
              isLoading: false,
            },
            this.updateFilter()
          );
        }
      }
    );
  }

  componentWillUnmount() {
    this.props.glEventHub.off(
      "toggleQuickFilterVisibility",
      this.toggleQuickFilterVisibility
    );
    this._isComponentMounted = false;
  }

  // handles hide/collapse for each filter group
  toggleFilterGroupVisibility = ({ groupKey }) => {
    this.setState((state) => ({
      [`${groupKey}_isOpen`]: !state[`${groupKey}_isOpen`],
    }));
  };

  // Render toggle button for button filter and have a handler to change filter applied state
  renderButtonFilter = (filter) => {
    const { classes } = this.props;
    const filterChange = () => {
      filter.isApplied = !filter.isApplied;
      this.updateFilter();
    };
    return (
      <ToggleButton
        className={classes.toggleButton}
        key={filter.label}
        selected={filter.isApplied}
        value="filter"
        disabled={filter.isDisabled}
        title={filter.description}
        classes={{
          root: classes.toggleButtonRoot,
          disabled: classes.toggleButtonDisabled,
          selected: classes.toggleButtonSelected,
          label: classes.toggleButtonLabel,
        }}
        onClick={filterChange}
      >
        {this.getIcon(filter.icon)}
        {filter.label}
      </ToggleButton>
    );
  };

  // Render toggle buttons for toggle filter and have a handler to change filter applied state
  renderSwitchFilter = (filter) => {
    const { classes } = this.props;
    const filterChange = (event, value) => {
      const currentOption = { ...value };
      filter.options.map((option) => {
        if (option.label === currentOption.label) {
          option.isApplied = !option.isApplied;
        } else if (!currentOption.isApplied && option.isApplied) {
          option.isApplied = !option.isApplied;
        }
      });
      this.updateFilter();
    };
    return (
      <ToggleButtonGroup
        exclusive
        key={filter.options.map((option) => option.label).join("_")}
        classes={{ root: classes.toggleButtonGroupRoot }}
        onChange={filterChange}
      >
        {filter.options.map((option) => (
          <ToggleButton
            className={classes.toggleButton}
            key={option.label}
            selected={option.isApplied}
            disabled={option.isDisabled}
            title={option.description}
            value={option}
            classes={{
              root: classes.toggleButtonRoot,
              disabled: classes.toggleButtonDisabled,
              selected: classes.toggleButtonSelected,
              label: classes.toggleButtonLabel,
            }}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  // returns icon for a filter
  getIcon = (filterIcon) => {
    return (
      filterIcon &&
      iconList[filterIcon] && (
        <i
          className={`${iconList[filterIcon]} ${this.props.classes.filterIcon}`}
        />
      )
    );
  };

  // Renders dropdown for select filter type
  renderDropdown = (filter) => {
    const filterTokens = filter.predicate.split(" ");
    let selectedValues =
      filterTokens.length > 2 ? JSON.parse(filterTokens[2]) : [];
    const dataSource = DataSourceList[filter.dataSource];
    return (
      <DataWrapperComponent
        key={filter.label}
        wrappedComponent={Select}
        componentProps={{
          onChange: (event) => this.onDropchanged(event, filter),
          selectedValues: selectedValues,
          fieldName: filter.fieldName,
          multipleSelection: filter.multipleSelection,
        }}
        {...dataSource}
        {...this.props.customStore}
      />
    );
  };

  // handles change in the value of a dropdown filter
  onDropchanged = (value, filter) => {
    const predicate = filter.predicate.split(" ");
    predicate[2] = `${JSON.stringify(value.map((v) => v.label))}`;
    filter.predicate = predicate.join(" ");
    filter.isApplied = Boolean(value && value.length);
    this.updateFilter();
  };

  renderFilterItem = (filter) => {
    switch (filter.actionType) {
      case "switch":
        return this.renderSwitchFilter(filter);
      case "select":
        return this.renderDropdown(filter);
      case "timerange":
        return this.renderTimeRangeEditor(filter);
      case "filter":
      default:
        return this.renderButtonFilter(filter);
    }
  };

  renderTimeRangeEditor = (filter) => {
    const filterChange = ({ predicate, isApplied }) => {
      filter.isApplied = isApplied;
      filter.predicate = predicate;
      this.updateFilter();
    };
    const predicate = filter.predicate.split(" ");
    const isInBetweenOperand = predicate.includes("inBetween");
    // This is to utilize the same time range input for multiple operand types like "InBetween","Lesser Than" and "GreaterThan" prsent in predicate.
    const time = isInBetweenOperand
      ? `${predicate[2].split("-")[0]} - ${predicate[2].split("-")[1]}`
      : `${predicate[2]} - ${predicate[6]}`;
    return (
      <TimeRange
        onClick={filterChange}
        filter={filter}
        defaultTime={time}
        isInBetweenOperand={isInBetweenOperand}
      />
    );
  };

  // Returns true if any individual filter within a group is Active
  isFilterGroupActive = (filterGroup) => {
    return filterGroup.filters.find((filter) => {
      if (filter.actionType === "switch") {
        return filter.options.find((option) => option.isApplied);
      } else {
        return filter.isApplied;
      }
    });
  };

  // Update the filter string in store based on active filters, which will trigger filter for shipments data
  updateFilter = () => {
    let quickFilter = appConstants.EMPTY_STRING;
    if (this.state.filterObject) {
      quickFilter = generateQuickFilterString(
        this.state.filterObject,
        this.props.userData.userProfile.planDate
      );
      this.setState((state) => ({
        filterObject: state.filterObject,
      }));
    }
    this.emitEvent("UpdateIBStore", "updateQuickFilter", quickFilter);

    const userProfile = this.props.userData.userProfile;
    quickFilter &&
      trackEvent("QuickFilter", userProfile, {
        quickFilter: quickFilter,
        filterName: this.props.filterName,
      });
  };

  // removes all active filters
  onClear = () => {
    if (this.state.filterObject) {
      this.setState((state) => ({
        filterObject: clearFilter(state.filterObject),
      }));
      this.emitEvent(
        "UpdateIBStore",
        "updateQuickFilter",
        appConstants.EMPTY_STRING
      );
    }
  };

  emitEvent = (storeName, eventName, eventData) => {
    if (this.props.isStoreEvent) {
      this.props.glEventHub.emit(storeName, eventName, [eventData]);
    } else {
      this.props.glEventHub.emit(eventName, {
        eventData,
        filterName: this.props.filterName,
      });
    }
  };

  // Toggles the visibility of quick filter panel
  toggleQuickFilterVisibility = (visible = null) => {
    const visibility = [true, false].includes(visible)
      ? visible
      : !this.state.quickFilterIsVisible;
    const width = visibility ? this.initialWidth : 40;
    this.props.glContainer.parent.container.setSize(width);

    this.setState({
      quickFilterIsVisible: visibility,
    });
  };

  //
  isAnyFilterApplied = () => {
    return (
      Object.keys(this.state.filterObject.filterGroups || {})
        .map((key) => {
          return this.isFilterGroupActive(
            this.state.filterObject.filterGroups[key]
          );
        })
        .filter((isActive) => isActive).length > 0
    );
  };

  render() {
    const { classes } = this.props;
    const { filterGroups } = this.state.filterObject || {};
    this.props.glContainer
      .getElement()
      .addClass(this.props.classes.quickFilterContainer);
    return this.state.quickFilterIsVisible ? (
      <Fragment>
        <header className={classes.header} data-automationid="quick-filter">
          <h3 className={classes.headerTitle}> Quick Filters </h3>
          <span className={classes.containerAction} onClick={this.onClear}>
            {" "}
            Clear All{" "}
          </span>
        </header>
        {this.state.isLoading ? (
          <div className={classes.message}>
            Please wait while loading filters
          </div>
        ) : (
          <div className={classes.container}>
            {this.state.filterObject ? (
              <List classes={{ root: classes.filterListContainer }}>
                {filterGroups ? (
                  Object.keys(filterGroups).map((filterGroupkey) => {
                    const filterGroup = filterGroups[filterGroupkey];
                    return (
                      <Fragment key={filterGroup.groupName}>
                        <ListItem
                          onClick={() =>
                            this.toggleFilterGroupVisibility({
                              groupKey: filterGroupkey,
                            })
                          }
                          classes={{ root: classes.filterGroupItem }}
                        >
                          <ListItemIcon
                            classes={{ root: classes.listItemIcon }}
                          >
                            {this.state[`${filterGroupkey}_isOpen`] ? (
                              <ExpandMore color="primary" />
                            ) : (
                              <ArrowRight color="primary" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            classes={{
                              root: classes.filterGroupTextRoot,
                              primary: classes.filterGroupText,
                            }}
                          >
                            {" "}
                            {filterGroup.groupName}{" "}
                          </ListItemText>
                          {this.isFilterGroupActive(filterGroup) &&
                            !this.state[`${filterGroupkey}_isOpen`] && (
                              <DotIcon
                                color="primary"
                                classes={{ root: classes.dotIcon }}
                              />
                            )}
                        </ListItem>
                        <Collapse
                          in={this.state[`${filterGroupkey}_isOpen`]}
                          classes={{
                            container: classes.collapseContainer,
                            wrapper: classes.filtersContainer,
                          }}
                        >
                          {filterGroup.filters &&
                            filterGroup.filters.map((filter) =>
                              this.renderFilterItem(filter)
                            )}
                        </Collapse>
                      </Fragment>
                    );
                  })
                ) : (
                  <div className={classes.message}> No filters available </div>
                )}
              </List>
            ) : (
              <div className={classes.message}>
                {" "}
                Error while fetching quick filters
              </div>
            )}
          </div>
        )}

        <footer
          className={classes.footer}
          onClick={this.toggleQuickFilterVisibility}
        >
          <ArrowLeft classes={{ root: classes.footerIcon }} />
          <div className={classes.footerText}>Hide Quick Filters</div>
        </footer>
      </Fragment>
    ) : (
      <Fragment>
        <div
          className={classes.headerPanelcontainer}
          onClick={this.toggleQuickFilterVisibility}
        >
          Quick Filters{" "}
          {this.isAnyFilterApplied() && (
            <span className={classes.filterApplied}>- Filter Applied</span>
          )}
        </div>
        <footer
          className={classes.footer}
          onClick={this.toggleQuickFilterVisibility}
        >
          <Tooltip delay={500} title={"Show Quick Filters"}>
            <ArrowRight classes={{ root: classes.footerIcon }} />
          </Tooltip>
        </footer>
      </Fragment>
    );
  }
}

export default compose(withStyles(styles), withUserProfile)(QuickFilter);
